import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { HttpStatusCode } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { Actions, ofType } from '@ngrx/effects';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { decode } from 'he';
import { take } from 'rxjs/operators';
import { CheckoutActions, CheckoutStoreFacade } from '@modules/checkout-store';
import { B2CEventsService } from './b2c/b2c-events.service';
import { InfoModalService } from '@modules/components-common';
import { LOGGER, BannerState, ENVIRONMENT } from '@modules/core';
import { CoreFacade } from '@modules/core/core.facade';
import { convertUrlToBreadcrumbStep } from './services/breadcrumb.service';
import {
  Environment,
  IBanner,
  LoggingService,
  ParentWebsite,
  Step,
} from './types';
import { getParentWebsite } from './util/check-bus-site';
import { getQueryValueFromQueryString } from './util/parse-query-string';
import { BASKET_ID_KEY, RouteNames } from './constants';

@Component({
  selector: 'nx-ng-bus-e-commerce-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'bus-e-commerce';
  dictionaryLoaded = false;
  isIframe = false;
  loginDisplay = false;

  public banner?: BannerState;

  public parentWebsite: ParentWebsite = 'west-midlands';

  private basketId: string | undefined = undefined;

  private currentUrl = '';

  //TODO: come from routing store
  currentPageTitle = 'Ticket selector';

  constructor(
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    @Inject(LOGGER) private logger: LoggingService,
    @Inject(ENVIRONMENT) public env: Environment,
    public coreFacade: CoreFacade,
    public checkoutFacade: CheckoutStoreFacade,
    public b2cEventsService: B2CEventsService,
    private authService: MsalService,
    private element: ElementRef,
    private gtmService: GoogleTagManagerService,
    private router: Router,
    private actions$: Actions,
    private infoModalService: InfoModalService
  ) {
    i18NextService.events.loaded.pipe((event) => {
      logger.logInfo(`i18next service loaded`, { source: 'app.component' });
      this.dictionaryLoaded = true;
      return event;
    });

    i18NextService.events.missingKey.subscribe((event) => {
      logger.logWarning(`i18next missing key`, {
        source: 'app.component',
        ...event,
      });
      return event;
    });

    const bannerInfoString = element.nativeElement.getAttribute(
      'data-banner'
    ) as string;

    this.coreFacade.setGtmEnabled(true);
    this.gtmService.addGtmToDom();

    this.parentWebsite = getParentWebsite();

    try {
      const decodedBanner = decode(bannerInfoString);
      const bannerData = <IBanner>JSON.parse(decodedBanner);
      this.coreFacade.setBannerDetail(bannerData);
    } catch (e) {
      logger.logError(
        {
          name: 'banner error',
          message: 'app.component: error initialising banner',
        },
        e as { [key: string]: unknown }
      );
    }

    checkoutFacade.selectBasketId$.subscribe((basketId: string | undefined) => {
      this.basketId = basketId;
    });

    b2cEventsService.redirectQueryString$.subscribe(
      (query: string | undefined) => {
        if (!query) {
          return;
        }
        const basketId = getQueryValueFromQueryString(query, 'basketId');
        if (basketId) {
          this.checkoutFacade.loadBasket(basketId);
        }
      }
    );

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
      }
    });
  }

  handleDismiss(): void {
    this.coreFacade.dismissBanner();
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;
    this.b2cEventsService.init();

    this.actions$
      .pipe(ofType(CheckoutActions.recreateBasketFromsAliasSuccess), take(1))
      .subscribe(() => {
        this.infoModalService.basket();
      });

    const basketID = localStorage.getItem(BASKET_ID_KEY);
    const urlParams = new URLSearchParams(window.location.search);
    const ticketID = urlParams.get('ticketID');

    if (ticketID) {
      this.checkoutFacade.loadFareByAliasAndAddToBasket(
        ticketID,
        basketID ? basketID : undefined
      );
    } else if (basketID) {
      this.checkoutFacade.loadBasket(basketID);
    }

    this.actions$
      .pipe(ofType(CheckoutActions.notifyBasketIsFull))
      .subscribe(() => {
        this.infoModalService.basketError({
          canRetry: false,
          errorText: 'basket_is_full_error',
        });
      });

    this.actions$
      .pipe(ofType(CheckoutActions.checkoutAPI_updateBasketFailure))
      .subscribe(() => {
        this.infoModalService.basketError({
          canRetry: true,
          errorText: 'technical_difficulties_subtext',
        });
      });
  }

  ngOnDestroy() {
    this.b2cEventsService.destroy();
  }

  getReturnUrl() {
    return this.currentUrl.includes(RouteNames.SEARCH_RESULT_TICKETS) ||
      this.currentUrl.includes(RouteNames.PAYG_TICKETS) ||
      this.currentUrl.includes(RouteNames.FLEXIBLE_TICKETS)
      ? `/${RouteNames.TICKET_TYPES}`
      : this.currentUrl;
  }

  login() {
    if (this.basketId) {
      this.authService.loginRedirect({
        state: `${this.getReturnUrl()}`,
        scopes: [],
      });
      return;
    }
    this.authService.loginRedirect({
      state: `${this.getReturnUrl()}`,
      scopes: [],
    });
  }

  logout() {
    this.authService.logoutRedirect();
  }

  handleDismissBanner(): void {
    this.coreFacade.dismissBanner();
  }

  get currentStep(): Step | undefined {
    const url = this.router.url;
    return convertUrlToBreadcrumbStep(url);
  }

  get theme(): string {
    if (this.parentWebsite === 'coventry') {
      return 'coventry';
    }
    return 'west-midlands';
  }
}
